import { VOIP_CONFIG } from 'config/voip';
import { IVehicle } from 'data/schema';
import { IPublicSession } from 'state/session';
import { user } from 'utils/auth/user';
import { jsonSafeParse } from 'utils/objects';
import { State } from 'utils/state';
import { tenantConfig } from 'utils/tenant';
import { IntegrationType, integrationType } from 'utils/ui/app';
import { EExperience } from 'data/schema';
import { getVoipSlotId } from 'utils/voipTabId';
import { voipMasterTabId } from './voipClaim';
import { getVoipClaimPending } from './voipClaimPending';

/**
 * Requirements that must be met before a session is ready for sharing
 */
interface ISessionReadyRequirements {
  // real-time syncing of session data is initialized
  broadcastingEstablished: boolean;

  // session state is saved to the server or successfully loaded from it (e.g. on reload)
  stateSynced: boolean;
}

/**
 * Keys that can be used to trigger session requirements
 */
export type TSessionRequirementKey = keyof ISessionReadyRequirements;

/**
 * Readiness pseudo-keys as emitted on change events by markRequirementReady — deliberately not
 * IPublicSession data. Owned here beside the type so listeners filter against one definition.
 */
export const READINESS_PSEUDO_KEYS: ReadonlySet<string> = new Set<TSessionRequirementKey>([
  'stateSynced',
  'broadcastingEstablished',
]);

/**
 * Keywords:
 * - session: a session in any state (started, in progress, ended, etc.). It has an ID, but nothing else guaranteed.
 * - presentation or experience: session that is in progress, customer is allowed to join, and all pre-requisites for the customer to see a 3D stream are met.
 *
 */
export class SessionState extends State<IPublicSession> {
  /**
   * Flags that match ISessionReadyRequirements will have to be set to true to confirm that they are ready
   */
  private $sessionReadyFlags: Partial<ISessionReadyRequirements> = {};

  markRequirementReady(requirement: TSessionRequirementKey): void {
    // Idempotent: only a real false→true flip emits
    if (this.$sessionReadyFlags[requirement]) {
      return;
    }
    const previous = this.$sessionReadyFlags[requirement];
    this.$sessionReadyFlags[requirement] = true;
    // setValues-parity positional shape, no trailing meta (flag flips are not resets). The
    // changed key is a readiness PSEUDO-key (TSessionRequirementKey, deliberately not part of
    // IPublicSession) so useState()/useAppState subscribers re-render off the derived getters.
    this.emit('change', [requirement], [true], [previous], {}, this.$data);
  }

  /**
   * Single clearing point for the readiness flags — runs on bare reset() AND on
   * setValues({ reset: true }) (which routes through this.reset()). Deliberately no emission
   * here: the reset path's change emit is setValues' job; a bare reset() stays silent.
   */
  reset(): void {
    super.reset();
    this.$sessionReadyFlags = {};
  }

  /**
   * Experiences, e.g. 2D can only be started when these requirements are met
   */
  get sessionReadyForExperience(): boolean {
    return !!this.get('prString') && !!this.$sessionReadyFlags['stateSynced'];
  }

  /**
   * Showroom streaming can start only when these requirements are met
   */
  get sessionReadyForStreaming(): boolean {
    return this.sessionReadyForExperience && !!this.$sessionReadyFlags['broadcastingEstablished'];
  }

  get hasStreamingId(): boolean {
    return !!this.get('aveSessionId') || !!this.get('aveChannelName');
  }

  get isLocalAveSession(): boolean {
    return this.get('sharingType') === 'broadcast-local' && this.get('isShared') && !!this.get('streamingDevice');
  }

  get isConnectedToLocalAve(): boolean {
    return this.isLocalAveSession && !!this.get('aveChannelName');
  }

  get isLocalAveDeviceLost(): boolean {
    return (
      !!this.get('isShared') &&
      this.get('sharingType') === 'broadcast-local' &&
      !!this.get('streamingDevice') &&
      !!this.get('localAveTakenOver')
    );
  }

  /**
   * PR review (AVS-4389): true when the dashboard's picked device (live `streamingDevice`)
   * differs from the COMMITTED device (the `snapshot` field share() writes atomically with
   * `isShared`) — i.e. a pick that hasn't been authorised via share() yet.
   */
  get hasPendingDeviceChange(): boolean {
    const selectedDeviceId = jsonSafeParse<{ id?: string }>(this.get('streamingDevice')).id;
    const snapshot = jsonSafeParse<IPublicSession>(this.get('snapshot') ?? '{}');
    const inPlayDeviceId = jsonSafeParse<{ id?: string }>(snapshot.streamingDevice ?? '').id;
    return !!selectedDeviceId && !!inPlayDeviceId && selectedDeviceId !== inPlayDeviceId;
  }

  get voipEnabled(): boolean {
    return this.get('sharingType') === 'remote' && tenantConfig().isAllowed('voip');
  }

  /**
   * AVS-4302: master/standby election is only active when both VOIP and the
   * single-stream flag are on. When this is false, `voipMasters` is ignored
   * entirely — a flag-off (or version-skewed) client behaves exactly as today.
   */
  get isVoipSingleStreamEnabled(): boolean {
    return this.voipEnabled && tenantConfig().isAllowed('voipSingleStream');
  }

  /**
   * AVS-4302: true iff this tab is the elected VOIP master for ITS dealer (its per-session
   * slot id, AVS-4454, matches the published `voipMasters[myPseudonym].tabId`).
   * Per-dealer keyed so a different dealer's entry never matches. Master-agnostic of
   * the flag: a tab that already claimed its slot is the master regardless, but a
   * standby is only derived when the feature is enabled (see `isVoipStandby`).
   */
  get amIMaster(): boolean {
    return voipMasterTabId(this.get('voipMasters')?.[user.userPseudonym]) === getVoipSlotId(this.get('id'));
  }

  /**
   * AVS-4302: true iff this tab should stay receive-only because another tab of
   * the SAME dealer is the elected master. Per-dealer keyed by `dealerPseudonym`, so
   * the same-dealer match is implicit in the key and a different dealer is never
   * suppressed (CG-1). Guarded by `isVoipSingleStreamEnabled`, so a flag-off client
   * ignores `voipMasters` and is never a standby (skew-safe).
   */
  get isVoipStandby(): boolean {
    if (!this.isVoipSingleStreamEnabled) {
      return false;
    }
    // A released slot (tombstone) is vacant, so it makes no tab a standby (AVS-4461).
    const holder = voipMasterTabId(this.get('voipMasters')?.[user.userPseudonym]);
    return !!holder && holder !== getVoipSlotId(this.get('id'));
  }

  /**
   * AVS-4461: true while this tab owns its dealer's slot through a claim made WITHOUT own media —
   * a standby that claimed a released slot (or the reload of one). It starts camera and mic only
   * when the dealer clicks "Take over"; until then it publishes nothing and presents no incoming
   * media. The marker must match the live slot (same session, this tab, same gen), so a slot that
   * moved on makes it inert. Flag-guarded like `isVoipStandby`: a flag-off tab always grabs the
   * camera. Deliberately NOT a standby: this tab is the owner, so it holds the inactivity timer
   * and the unload rule.
   */
  get isVoipClaimPendingMedia(): boolean {
    if (!this.isVoipSingleStreamEnabled || !this.amIMaster) {
      return false;
    }
    const marker = getVoipClaimPending();
    const slot = this.get('voipMasters')?.[user.userPseudonym];
    return !!marker && marker.sessionId === this.get('id') && marker.gen === slot?.gen;
  }

  /**
   * AVS-4461: true while this tab runs VOIP without its own camera and mic — a standby, or an
   * owner whose claim waits for the "Take over" click. The one definition of "no own media".
   */
  get isVoipWithoutOwnMedia(): boolean {
    return this.isVoipStandby || this.isVoipClaimPendingMedia;
  }

  /**
   * AVS-4302: whether this tab should publish its own VOIP media. Flag-off ⇒ always
   * true (today's behaviour: every tab publishes). With the election on, a customer
   * always publishes, and a dealer publishes UNLESS it is a standby — i.e. unless
   * another tab of the SAME dealer owns the master slot. Suppression is identity-scoped
   * via `isVoipStandby`: a DIFFERENT dealer (foreign master) is never a standby, so it
   * still publishes (the spec requires each distinct dealer to stream — only same-dealer
   * duplicates are collapsed). Using `!isVoipStandby` rather than `amIMaster` is the fix:
   * `amIMaster` is tab-scoped and wrongly suppressed a second distinct dealer. The
   * fallback claim (`claimVoipMasterIfVacant`) runs before the gate reads this, so a
   * dealer that reaches the gate with a vacant slot becomes master and therefore publishes —
   * unless that claim is pending its media (AVS-4461), which waits for "Take over".
   */
  get shouldPublishVoip(): boolean {
    return !this.isVoipSingleStreamEnabled || user.type === 'customer' || !this.isVoipWithoutOwnMedia;
  }

  /**
   * AVS-4302: true iff this tab must not PRESENT incoming VOIP media — a standby renders
   * "playing in another tab" placeholders instead of remote video/audio so participants
   * are never seen or heard twice across a dealer's tabs. The RTC-level enforcement is
   * keyed independently on `RTCClient.receiveOnly`, so the two layers cannot deadlock.
   *
   * Bypassed while `VOIP_CONFIG.loopbackVideo` is on: that debug flag exists to inspect
   * live loopback streams, so a standby then plays remote media in BOTH tabs (double
   * audio included) — do not expect single-audio to hold with the flag flipped.
   *
   * AVS-4461: a pending owner is suppressed the same way — it renders the "Take over" tile.
   */
  get isVoipIncomingMediaSuppressed(): boolean {
    return this.isVoipWithoutOwnMedia && !VOIP_CONFIG.loopbackVideo;
  }

  get presentationStarted(): boolean {
    const session = this.get();
    return Boolean(
      (session.activeExperience === '2D' && session.carImagesStatus === 'ready') || this.showroomConnected
    );
  }

  /**
   * AVS-4355: entering fullscreen requires a live presentation, but leaving it never does.
   * Gating the exit control on `presentationStarted` alone trapped users in the overlay
   * when the presentation ended under them (3D disabled, 2D image failed to load).
   */
  get canToggleFullScreen(): boolean {
    return this.presentationStarted || Boolean(this.get('streamFullScreen'));
  }

  // This is a brief period of time while the showroom is connecting, but not yet connected. aveSessionId not yet available
  get showroomConnecting(): boolean {
    const session = this.get();
    return Boolean(session.activeExperience === '3D' && !this.hasStreamingId && session.streamActive);
  }

  get showroomConnected(): boolean {
    const session = this.get();
    return Boolean(session.activeExperience === '3D' && this.hasStreamingId && session.streamActive);
  }

  get canDownload3dSnapshot(): boolean {
    return tenantConfig().isAllowed('download3dSnapshot') && this.showroomConnected;
  }

  get canDownload2dImage(): boolean {
    return (
      tenantConfig().isAllowed('download2dImage') &&
      tenantConfig().isAllowed('2D') &&
      this.get('activeExperience') === '2D' &&
      this.get('comparisonMode') !== 'on'
    );
  }

  get canDownloadTransparent2dImage(): boolean {
    return (
      this.get('brand') === 'A' &&
      tenantConfig().isAllowed('downloadTransparent2dImage') &&
      tenantConfig().isAllowed('2D') &&
      this.get('activeExperience') === '2D' &&
      this.get('carStatus') === 'ready'
    );
  }

  get canDownloadComparisonImage(): boolean {
    return (
      tenantConfig().isAllowed('downloadComparisonImage') &&
      tenantConfig().isAllowed('2D') &&
      this.get('comparisonMode') === 'on'
    );
  }

  /**
   * Whether any download feature flag is enabled in tenant config.
   * Unlike the `can*` getters, this only checks raw flags — not runtime conditions
   * like active experience or stream connectivity.
   */
  get hasAnyDownloadFlag(): boolean {
    const tc = tenantConfig();
    return (
      tc.isAllowed('download2dImage') ||
      tc.isAllowed('downloadTransparent2dImage') ||
      tc.isAllowed('download3dSnapshot') ||
      tc.isAllowed('downloadComparisonImage')
    );
  }

  /**
   * Determines if the sharing dashboard button can be opened.
   *
   * This is a simple check for UI button enabling:
   * - Must have a session
   * - Car images must not be loading
   * - If 3D is active but not connected and 2D is disabled, cannot open
   */
  get canOpenSharingDashboard(): boolean {
    const session = this.get();

    if (!session.id) return false;
    if (session.carImagesStatus === 'loading') return false;

    // If 3D is active but not connected and 2D is disabled, cannot share (no fallback)
    if (session.activeExperience === '3D' && !this.showroomConnected && !tenantConfig().isAllowed('2D')) {
      return false;
    }

    return true;
  }

  /**
   * Determines if sharing can be started.
   *
   * This checks all conditions needed to actually start a sharing session:
   * - Session must be ready (has ID and prString)
   * - Different requirements based on sharing type and whether streaming is required
   */
  get canStartSharing(): boolean {
    const session = this.get();

    // Must be ready for sharing (has ID and prString)
    if (!session.id || !session.prString) {
      return false;
    }

    const hasStreamingDevice = !!session.streamingDevice;
    const isLocalRenderingAvailable = tenantConfig().isAllowed('sharingBroadcastLocal');
    const streamIsRequired = !isLocalRenderingAvailable;

    if (streamIsRequired) {
      const isCarReady = session.carStatus === 'ready';
      if (session.sharingType === 'broadcast') {
        return isCarReady && hasStreamingDevice;
      }
      return isCarReady;
    }

    if (session.sharingType === 'broadcast-local') {
      return hasStreamingDevice;
    }

    return true;
  }

  get customerCanAccessPresentation(): boolean {
    return (
      this.get('customerAllowed') &&
      ((this.get('activeExperience') === '3D' && this.get('streamActive')) || this.get('activeExperience') === '2D')
    );
  }

  /**
   * If it is a broadcast or a device session, we don't want to show the hangup button in the customer view
   */
  get customerCanSeeHangupButton(): boolean {
    return !(
      user.type === 'customer' &&
      (this.get('sharingType') === 'broadcast' || this.get('sharingType') === 'device')
    );
  }

  get sessionEnded(): boolean {
    return !!this.get('endedAt') && !this.get('streamActive');
  }

  /**
   * Session has once started, shared and ended
   */
  get presentationCompleted(): boolean {
    return this.sessionEnded && this.get('isShared');
  }

  /**
   * Session was never shared when it was ended
   * it has ended, but never shared. However, if scheduled time is added then its not abandoned, but delayed
   */
  get sessionAbandoned(): boolean {
    return !!this.get('endedAt') && !this.get('isShared') && !this.get('scheduledStartTime'); // no rating for this session
  }

  /**
   * Session is scheduled and hasn't started yet
   */
  get sessionScheduled(): boolean {
    return !this.get('endedAt') && !this.get('isShared') && !!this.get('scheduledStartTime');
  }

  /**
   * Session is started and then ended, but rating is not completed
   */
  get presentationEndedWithoutRating(): boolean {
    return Boolean(this.get('id') && !this.get('ratingCompleted'));
  }

  get sessionEligibleForRating(): boolean {
    const isDealer = user.type === 'dealer';
    const isCustomer = user.type === 'customer';

    // [AVS-3728] Nerd sessions should not have a rating modal for customers
    const isBroadcastSession = this.get('sharingType') === 'broadcast';
    const isCustomerAndNotBroadcast = isCustomer && !isBroadcastSession;

    // For dealers, the session must have ended and there should not be a rating already
    const isDealerAndPresentationEnded = isDealer && this.presentationEndedWithoutRating;

    return isCustomerAndNotBroadcast || isDealerAndPresentationEnded;
  }

  get dealerWaitingRoomEligible() {
    return (
      !this.dealerReadyToPresent &&
      this.get('isShared') &&
      this.get('sharingType') === 'remote' &&
      !this.presentationCompleted
    );
  }

  get dealerReadyToPresent() {
    return tenantConfig().isAllowed('voip') ? this.get('customerAllowed') : true;
  }

  get isSessionDgaEligible() {
    // [AVS-3845] DGA shows even if the dealer ends stream that was never shared
    return (
      // okapi/vin/marketing code exists
      !!this.get('vinOrMarketingCode') &&
      // sharingDga is enabled in tenant config
      tenantConfig().isAllowed('sharingDga') &&
      // the user is a dealer
      user.type === 'dealer' &&
      // [AVS-4270] an outdated (2D-only) car cannot be handed to the 3D giveaway
      !this.is3DBlocked
    );
  }

  /**
   * DGA can be created, but hasn't been created yet
   */
  get dgaPending() {
    return Boolean(this.isSessionDgaEligible && !this.get('dgaLink'));
  }

  get dgaCreated() {
    return Boolean(this.isSessionDgaEligible && !!this.get('dgaLink'));
  }

  /**
   * Determines whether the end session button should be enabled.
   *
   * The button is enabled when the car status is 'ready' AND either:
   * - The application is running as a web component, OR
   * - The application is standalone but not currently on the goodbye page
   *
   * The function is agnostic of React Router because RR cannot be used in web components.
   *
   * @returns True if the end session button should be enabled, false otherwise
   */
  get endSessionButtonEnabled() {
    const isCarReady = this.get('carStatus') === 'ready';
    const isWebComponent = integrationType() === IntegrationType.WebComponent;
    const isOnGoodbyePage = globalThis.location.pathname.endsWith('/goodbye');
    // customer accessing sessions that are scheduled, but not yet started should not see the end session button
    const isScheduledForCustomer = this.sessionScheduled && user.type === 'customer';

    // Show button if car is ready AND (it's a web component OR standalone, but not on goodbye page)
    return isCarReady && (isWebComponent || !isOnGoodbyePage) && !isScheduledForCustomer;
  }
  get currentVehicle(): IVehicle | undefined {
    const vehicles = this.get('vehicles');
    const activeVehicleId = this.get('activeVehicleId');
    if (!vehicles || activeVehicleId == null) {
      return undefined;
    }
    return vehicles[activeVehicleId];
  }

  /**
   * AVS-4270: the ACTIVE session vehicle is an outdated (3+ years) car served for
   * 2D-only display. Blocks solely on the server-set `outdated3D` marker (never
   * client-side age math — OKAPI/CASA cars without modelYear stay untouched); the
   * flag checks mirror the server-side gate (both flags required — checking only
   * outdatedVehicles would fight carImages' 2D-disabled → 3D forcing).
   */
  get is3DBlocked(): boolean {
    return (
      this.get('carDetails')?.outdated3D === true &&
      tenantConfig().isAllowed('outdatedVehicles') &&
      tenantConfig().isAllowed('2D')
    );
  }

  /**
   * AVS-4335: the LocalAVE renderer (sharing type 'broadcast-local') can only
   * display a 3D stream — 2D is never selectable while it is the active sharing option.
   * AVS-4362: narrow exception — when the device has been taken over and 2D is available,
   * 2D fallback is permitted so the session can continue without the device.
   */
  get is2DBlocked(): boolean {
    if (this.isLocalAveDeviceLost && tenantConfig().isAllowed('2D')) {
      return false;
    }
    return this.get('sharingType') === 'broadcast-local';
  }

  /**
   * Get the selected experience (2D/3D) based on session state and tenant config.
   *
   * Business Rules:
   * 0. Forces 2D when comparison mode is enabled (and 2D is allowed) — comparison is a
   *    2D-only view and the local renderer can never be STARTED from it (AVS-4354: the
   *    menu option, the start CTA and share() are all guarded), so it takes precedence
   *    over every 3D-forcing rule, including the LocalAVE lock below.
   * 1. Forces 2D when the LocalAVE device was taken over and images are ready (AVS-4362)
   *    — the session continues without the device.
   * 2. Forces 3D when the LocalAVE sharing option is active (AVS-4335) — takes
   *    precedence over Rule 3 because LocalAVE can never render 2D, whereas an
   *    outdated vehicle's 2D-only constraint is a rendering preference, not a hard
   *    limit of the active renderer.
   * 3. Forces 2D when the active vehicle is blocked from 3D (outdated car, AVS-4270)
   * 4. Forces 3D when car images aren't ready or 2D is disabled
   * 5. Otherwise uses the default experience from config
   */
  get selectedExperience(): EExperience {
    const { config } = tenantConfig();
    const session = this.get();
    const isCarReady = session.carStatus === 'ready';

    // Rule 0: When comparison mode is enabled, force 2D if allowed — wins over the
    // LocalAVE 3D lock because LocalAVE cannot be started from comparison (AVS-4354)
    if (session.comparisonMode === 'on' && tenantConfig().isAllowed('2D')) {
      return '2D';
    }

    // Rule 1 (device-lost): LocalAVE device was taken over — fall back to 2D if available
    // and images are ready (AVS-4362)
    if (this.isLocalAveDeviceLost && tenantConfig().isAllowed('2D') && session.carImagesStatus === 'ready') {
      return '2D';
    }

    // Rule 2: LocalAVE cannot render 2D — locked to 3D, even for outdated vehicles
    if (this.is2DBlocked) {
      return '3D';
    }

    // Rule 3: an outdated (3D-blocked) vehicle always defaults to 2D
    if (this.is3DBlocked) {
      return '2D';
    }

    // Rule 4: When no images are loaded or 2D is disabled, force 3D
    if ((session.carImagesStatus !== 'ready' || !tenantConfig().isAllowed('2D')) && isCarReady) {
      return '3D';
    }

    // Rule 5: Otherwise use default from config
    return config.defaultExperience;
  }
}
